import { useState } from 'react';
import { PostPortfolioItemType, SymbolChart } from 'common/interfaces/api';
import MainLayout from 'components/v2/layouts/main-layout';
import LeftBarSection from 'components/v2/modules/left-bar/left-bar-section';
import DemoWarningBanner from 'components/v2/modules/portfolio/demo-warning-banner';
import PortfolioPositionsSection from 'components/v2/modules/portfolio/portfolio-positions-section';
import PortfolioSummaryCard from 'components/v2/modules/portfolio/portfolio-summary-card';
import RightSidebar from 'components/v2/modules/portfolio/right-sidebar';
import BuySellDialog from 'components/v2/modules/trading-demo/buy-sell-dialog';
import ChooseSymbolDialog from 'components/v2/modules/trading-demo/choose-symbol-dialog';
import OrderSuccessPopup, {
  OrderSuccessPopupType,
} from 'components/v2/modules/trading-demo/order-success-popup';
import LoginSignupPR from 'components/v2/organisms/login-signup-pr';
import { useQuery } from 'react-query';
import { callGetPortfolioInfoRequest } from 'service/trading-demo';
import useGlobalStore from 'store/global';

import QuickStats from './quick-stats';

const PortfolioPageContent = () => {
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const [isChooseSymbolDialogOpen, setIsChooseSymbolDialogOpen] =
    useState(false);
  const [currentSymbol, setCurrentSymbol] =
    useState<PostPortfolioItemType | null>(null);
  const [successPopupData, setSuccessPopupData] =
    useState<OrderSuccessPopupType | null>(null);

  const {
    data: portfolioInfoData,
    refetch: refetchPortfolioInfoData,
    isFetched: isPortfolioInfoDataFetched,
  } = useQuery(
    ['portfolioInfoData', currentAuth?.uid?.toString()],
    async () => {
      if (!currentAuth || !isAuthInit) return Promise.reject();
      return await callGetPortfolioInfoRequest({
        includePositions: true,
        includeTransactions: true,
      });
    },
    {
      enabled: !!currentAuth && isAuthInit,
      refetchOnWindowFocus: false,
    },
  );

  const handleSymbolSelect = (symbol: SymbolChart) => {
    const portfolioItem: PostPortfolioItemType = {
      symbol_id: symbol.id,
      symbol_name: symbol.name,
      latest_price: symbol.price.toString(),
      price_change_pct: symbol.change.toString(),
      position_type: null,
      quantity: null,
      unrealized_pnl: null,
      currency: symbol.currency || '',
    };
    setCurrentSymbol(portfolioItem);
    setIsChooseSymbolDialogOpen(false);
  };

  return (
    <div>
      <MainLayout>
        <div className="all-font-sans grid grid-cols-mainMd grid-rows-mainMd grid-areas-mainMd lg:grid-cols-mainLg lg:grid-rows-mainLg lg:grid-areas-mainLg xl:grid-cols-mainXl xl:grid-rows-mainXl xl:grid-areas-mainXl">
          <LeftBarSection />
          <main className="mx-auto w-full min-w-0 max-w-full px-3 pb-20 pt-4 grid-in-mainContent md:w-[600px] md:max-w-[600px] md:px-5">
            {!currentAuth && <LoginSignupPR className="mb-6" />}
            {/* Demo Warning Banner */}
            <DemoWarningBanner />

            {/* Portfolio Summary */}
            <PortfolioSummaryCard
              totalValue={portfolioInfoData?.data?.summary?.total_value || '0'}
              change={
                portfolioInfoData?.data?.summary?.total_unrealized_pnl || '0'
              }
              changePercent={
                portfolioInfoData?.data?.summary?.total_unrealized_pnl_pct ||
                '0'
              }
              balance={portfolioInfoData?.data?.summary?.cash_balance}
              isPortfolioInfoDataFetched={
                isPortfolioInfoDataFetched || !currentAuth
              }
            />

            <div className="mb-5 xl:hidden">
              <QuickStats portfolioData={portfolioInfoData} />
            </div>

            {/* Portfolio Positions */}
            <PortfolioPositionsSection
              portfolioData={portfolioInfoData}
              refetchPortfolioInfoData={refetchPortfolioInfoData}
              isPortfolioInfoDataFetched={
                isPortfolioInfoDataFetched || !currentAuth
              }
            />

            {/* Strategy Hint */}
            {/* <div className="my-5 flex items-center gap-2 rounded-xl bg-[#FFF9E5] p-4 text-sm">
              <span>💡</span>
              <span className="text-[13px] text-pp-text-secondary">
                ダンの戦略: センチメント35%以下でロング、65%以上でショート
              </span>
            </div> */}

            {/* CTA Button */}
            <div className="mt-5">
              <button
                onClick={() => {
                  if (!currentAuth) {
                    showLogin({});
                    return;
                  }
                  setIsChooseSymbolDialogOpen(true);
                }}
                className="w-full rounded-xl bg-pp-green-primary p-4 text-base font-semibold text-white transition-colors hover:bg-green-600"
              >
                デモトレード銘柄一覧
              </button>
            </div>

            {/* Compliance Footer */}
            <div className="mt-10 p-3 text-xs leading-relaxed text-pp-text-tertiary md:p-5">
              <p className="mb-2 font-semibold">重要事項：</p>
              <ul className="mb-2 list-disc space-y-1 pl-5 text-[11px]">
                <li>
                  本機能は教育目的のデモトレード環境です。実際の金銭取引は発生しません。
                </li>
                <li>
                  表示される価格は実際の市場価格を基にしていますが、リアルタイムではなく遅延したデータです。
                  システム障害やデータの遅延・誤りによりデモトレードが正常に動作しない場合があります。
                </li>
                <li>
                  TakaTradeはPostPrime株式会社のグループ会社ですが、本デモトレード機能はTakaTradeでの口座開設を条件とするものではありません。実際の取引を行う金融商品取引業者の選択は、ユーザー自身の判断で行ってください。
                </li>
              </ul>
            </div>
          </main>
          <RightSidebar portfolioData={portfolioInfoData} />
        </div>
      </MainLayout>

      {/* Dialogs */}
      {isChooseSymbolDialogOpen && (
        <ChooseSymbolDialog
          isOpen={isChooseSymbolDialogOpen}
          onSelect={handleSymbolSelect}
          onClose={() => setIsChooseSymbolDialogOpen(false)}
        />
      )}
      {currentSymbol && (
        <BuySellDialog
          currentSymbol={currentSymbol}
          onClose={() => setCurrentSymbol(null)}
          setSuccessPopupData={setSuccessPopupData}
          title="新規デモトレード"
          refetchPortfolioInfoData={refetchPortfolioInfoData}
        />
      )}
      {successPopupData && (
        <OrderSuccessPopup
          data={successPopupData}
          onClose={() => setSuccessPopupData(null)}
        />
      )}
    </div>
  );
};

export default PortfolioPageContent;
